import { useMemo } from 'react';
import { useGetServices, useGetSessions } from '@/services';

const normalizeText = (value: string) =>
	value
		.toLocaleLowerCase('pt-BR')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');

const isPsychologistService = (
	service: Pick<Services.Service, 'service_name' | 'professional_label'>,
) =>
	normalizeText(
		`${service.service_name} ${service.professional_label ?? ''}`,
	).includes('psicolog');

/**
 * Última sessão de PSICÓLOGO já realizada (FINISHED) pelo usuário logado.
 * Base do card "Tudo pronto para continuar com X?" da home, que sugere
 * agendar de novo com o mesmo profissional.
 */
export const useLastFinishedPsychologistSession = () => {
	const { data: services, isLoading: isLoadingServices } = useGetServices({
		filters: { active: true, type: 'PROFESSION' },
	});

	const psychologistService = useMemo(
		() => (services ?? []).find(isPsychologistService) ?? null,
		[services],
	);

	const { data, isLoading: isLoadingSessions } = useGetSessions(
		{
			isMe: true,
			limit: 10,
			page: 1,
			statusList: ['FINISHED'],
			sort: 'selected_date',
			sort_direction: 'desc',
		},
		{ refetchOnMount: 'always' },
	);

	const session = useMemo(() => {
		const list = data?.data ?? [];
		if (psychologistService) {
			return (
				list.find((s) => s.service_id === psychologistService.id) ??
				null
			);
		}
		return (
			list.find((s) =>
				normalizeText(s.service_name ?? '').includes('psicolog'),
			) ?? null
		);
	}, [data?.data, psychologistService]);

	return {
		session,
		service: psychologistService,
		isLoading: isLoadingServices || isLoadingSessions,
	};
};
